import { EyeOutlined } from '@ant-design/icons';
import type { TableColumnsType, TableProps } from 'antd';
import { Button, Space, Table, Tag } from 'antd';
import { Form, Formik } from 'formik';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from '../../../config/config';
import useQueryParams, { buildParamsFromQueryKernel, buildParamsFromQueryReceivedParams } from '../../../hooks/useQueryParams';
import OrderModel from '../../../models/OrderModel';
import { PermissionCustomer } from '../../../permissions/PermissionCustomer';
import { PermissionOrder } from '../../../permissions/PermissionOrder';
import orderStore from '../../../stores/orderStore';
import { formatDateString } from '../../../utils/dateFunctions';
import { exportToExcel } from '../../../utils/excelFunction';
import { convertToDayjs, formatCurrency, formattedDates, getFirstFilterDates, getSortField, getSortOrder, getUniquePropertyFilters, valueStatusConvert } from '../../../utils/functions';
import { orderTypes } from '../../../utils/selects';
import { getOrderStatusColor } from '../../../utils/statusColorMapper';
import BreadCrumbComponent from '../../atoms/BreadCrumbComponent';
import ActionButton from '../../atoms/ButtonComponent';
import DatePickerComponet from '../../atoms/DatePickerComponet';
import InputComponet from '../../atoms/InputComponet';
import PermissionComponent from '../../atoms/PermissionComponent';
import SelectComponent from '../../atoms/SelectComponent';
import ContentBox from '../../molecules/ContentBox';
import Permission from '../../molecules/Permission';
import ModalCliente from '../../organisms/ModalCliente';
import { useAuth } from '../../protected/ProviderAuth';

interface DataType {
    status: any;
    totalBilled: any;
    total: any;
    paymentDetails: any;
    uuid: any;
    key: React.Key;
    idOrder: string;
    externalId: string;
    customer: any;
}

interface QueryParams {
    name?: string;
    code?: string;
    size?: number;
    page?: number;
    sort?: string;
    search?: string;
    statusName?: string;
    active?: string;
}

interface FormValues {
    idOrder: string;
    customerName: string; // Ajuste para o Select
    bonus: { label: string; value: string }; // Ajuste para o Select
    statusName: { label: string; value: string }; // Ajuste para o Select
    externalId: string;
    customerDocument: string;
    date: any;
}

// BreadCrumb
const breadCrumb = [
    {
        title: <Link to="/dashboard">Dashboard</Link>,
    },
    {
        title: <p>Listagem de Pedidos</p>,
    },
]

export const orderStatus = [
    { id: '1', value: 'ANALYSIS', label: 'Pedido Realizado' },
    { id: '2', value: 'PAID', label: 'Pagamento Autorizado' },
    { id: '4', value: 'BILLED', label: 'Pedido Faturado' },
    { id: '5', value: 'SENT', label: 'Pedido Saiu Para Entrega' },
    { id: '6', value: 'DELIVERED', label: 'Pedido Entregue' },
    { id: '9', value: 'CANCELED', label: 'Pedido Cancelado' },
];

const ListOrder: React.FC = observer(() => {
    const { orderList, loading, page, totalElements, size, defaultparams, sort } = orderStore;
    const [itemSelected, setItemSelected] = useState<any>();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [queryParams, setQueryParams] = useQueryParams();
    const { hasPermission, isColumnVisible } = useAuth();
    const navigate = useNavigate();
    const [initialDate, finalDate] = getFirstFilterDates();

    const mappings = {
        page: 'page',
        size: 'size',
        sort: 'created,desc',
        idOrder: 'idOrder',
        customerName: 'customerName',
        customerDocument: 'customerDocument',
        bonus: 'bonus',
        statusName: 'statusName',
        externalId: 'externalId'
    };

    const paramsDefault = {
        size: DEFAULT_PAGE_SIZE,
        page: 0,
        sort: 'created,desc',
        search: queryParams.statusName ? '' : 'statusName!CART',
    };

    useEffect(() => {
        const params = buildParamsFromQueryKernel(queryParams, mappings);

        const defaultParams = {
            ...params,
            from: queryParams.from ? queryParams.from + ' 00:00:00.556' : initialDate + ' 00:00:00.556',
            to: queryParams.to ? queryParams.to + ' 23:59:59' : finalDate + ' 23:59:59',
        }

        const fetchData = async () => {
            if (hasPermission(PermissionOrder.GET)) {
                await orderStore.getList(defaultParams);
            }
        };

        fetchData();
    }, []);

    // Colunas da Tabela
    const columns: TableColumnsType<DataType> = [
        {
            title: 'Número',
            dataIndex: 'id_order',
            key: 'id_order',
            defaultSortOrder: getSortOrder('id_order', queryParams.sort),
            sorter: true,
            render: (item, record) => {
                return (
                    <>{record && <p>{record?.idOrder}</p>}</>
                );
            },
        },
        {
            title: 'Cliente',
            dataIndex: 'c.first_name', // Verifique a estrutura dos dados
            key: 'c.first_name',
            filters: getUniquePropertyFilters<any, 'customer', 'firstName'>(orderList, 'customer', 'firstName'),
            onFilter: (value, record) => record.customer.firstName.indexOf(value as string) === 0, // Acesse o nome do canal
            sorter: true,
            defaultSortOrder: getSortOrder('c.first_name', queryParams.sort),
            render: (item, record) => {
                return (
                    <>{record && <p>{record?.customer?.firstName}</p>}</>
                );
            },
        },
        {
            title: 'Documento',
            dataIndex: 'c.document',
            key: 'c.document',
            defaultSortOrder: getSortOrder('c.document', queryParams.sort),
            sorter: true,
            render: (item, record) => {
                return (
                    <>{record && <p>{record?.customer?.document}</p>}</>
                );
            },
        },
        {
            title: 'Tipo de Pedido',
            dataIndex: 'bonus',
            key: 'bonus',
            sorter: true,
            render: (item) => {
                return (
                    <>{<p>{item === true ? 'Bônus' : "Venda"}</p>}</>
                );
            },
        },
        {
            title: 'Valor Previo',
            dataIndex: 'total',
            key: 'total',
            defaultSortOrder: getSortOrder('total', queryParams.sort),
            sorter: true,
            render: (total) => {
                return (
                    <>{total && <p title={total}>{formatCurrency(total)}</p>}</>
                );
            },
        },
        {
            title: 'Data de criação',
            dataIndex: 'created',
            key: 'created',
            sorter: true,
            defaultSortOrder: getSortOrder('created', queryParams.sort),
            render: (text) => <p>{formatDateString(text)}</p>,
        },
        {
            title: 'Status',
            dataIndex: 'os.status_name',
            key: 'os.status_name',
            sorter: true,
            defaultSortOrder: getSortOrder('os.status_name', queryParams.sort),
            render: (text, record) => {
                return (
                    <Tag color={getOrderStatusColor(record?.status?.statusName)}><p className="text-black">{valueStatusConvert(record?.status?.statusName)}</p></Tag>
                );
            },
        },
        {
            title: 'Visualizar Pedido',
            dataIndex: 'order',
            key: 'order',
            align: 'center',
            width: 50,
            hidden: !isColumnVisible(PermissionOrder.GET_UUID),
            render: (text, record) => (
                <Space className="cursor-pointer" onClick={() => navigate(`/detalhes-pedido/customer/${record?.customer?.uuid}/order/${record?.uuid}`)}>
                    <div><EyeOutlined /></div>
                </Space>
            ),
        },
        {
            title: 'Visualizar Cliente',
            dataIndex: 'client',
            key: 'client',
            align: 'center',
            width: 50,
            hidden: !isColumnVisible(PermissionCustomer.GET_UUID),
            render: (text, record) => (
                <Space className="cursor-pointer">
                    <div onClick={() => showModal(record)}>
                        <div><EyeOutlined /></div>
                    </div>
                </Space>
            ),
        },
    ];

    const showModal = (record: any) => {
        setItemSelected(record);
        setIsModalOpen(true);
    };

    const initialValues = {
        idOrder: Array.isArray(queryParams.idOrder) ? queryParams.idOrder[0] || '' : queryParams.idOrder || '',
        customerName: Array.isArray(queryParams.customerName) ? queryParams.customerName[0] || '' : queryParams.customerName || '',
        bonus: typeof queryParams.bonus === 'string'
            ? { value: queryParams.bonus, label: queryParams.bonus }
            : { value: '', label: '' },
        statusName: typeof queryParams.statusName === 'string'
            ? { value: queryParams.statusName, label: queryParams.statusName }
            : { value: '', label: '' },
        from: queryParams.from ? queryParams.from + ' 00:00:00.556' : initialDate + ' 00:00:00.556',
        to: queryParams.to ? queryParams.to + ' 23:59:59' : finalDate + ' 23:59:59',
        date: queryParams.from && queryParams.to ? [convertToDayjs(queryParams.from), convertToDayjs(queryParams.to)] : [convertToDayjs(initialDate), convertToDayjs(finalDate)],
        externalId: Array.isArray(queryParams.externalId) ? queryParams.externalId[0] || '' : queryParams.externalId || '',
        customerDocument: Array.isArray(queryParams.customerDocument) ? queryParams.customerDocument[0] || '' : queryParams.customerDocument || '',
        customer_clientExternal: Array.isArray(queryParams.customer_clientExternal) ? queryParams.customer_clientExternal[0] || '' : queryParams.customer_clientExternal || '',
    };

    const handleSubmit = async (values: FormValues) => {
        const formattedDate = formattedDates(values.date, 'YYYY-MM-DD');
        const dateInitial = formattedDate[0];
        const dateFinal = formattedDate[1];

        const newParams: any = {
            idOrder: values.idOrder || undefined,
            externalId: values.externalId || undefined,
            customerName: values?.customerName || undefined,
            bonus: values?.bonus?.value || undefined,
            statusName: values?.statusName?.value || undefined,
            customerDocument: values?.customerDocument || undefined,
            from: dateInitial + ' 00:00:00.556',
            to: dateFinal + ' 23:59:59',
            sort: queryParams.sort || null,
        };

        setQueryParams(newParams);
        await orderStore.getList(newParams);
    };

    const handleClearAndSearch = async (resetForm: () => void) => {
        resetForm();
        setQueryParams({});
        await orderStore.getList(defaultparams);
    };

    const onChange: TableProps<DataType>['onChange'] = async (pagination, filters, sorter, extra) => {
        const params = buildParams(pagination, sorter, queryParams)
        await orderStore.getList(params);
    };

    const buildParams = (
        pagination: any,
        sorter: any,
        queryParams: QueryParams
    ) => {
        const getSort = getSortField(sorter);
        const currentPage = pagination.current - 1;
        const newPageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;

        const params = {
            ...queryParams,
            size: pagination.pageSize || queryParams.size || newPageSize,
            page: pagination.current ? currentPage : queryParams.page || 0,
            sort: getSort || queryParams.sort || sort || 'created,desc',
        };

        // Atualiza a URL com os novos parâmetros
        setQueryParams({
            size: params.size,
            page: params.page,
            sort: params.sort,
        });

        return params;
    };

    const handleExport = async () => {
        const params = buildParamsFromQueryReceivedParams(queryParams, mappings, paramsDefault, ":", ',');
        const exportParams = {
            ...params,
            size: 999999,
        }

        const dataExport = await orderStore.getList(exportParams);

        if (dataExport) {
            const listExport = dataExport.data.content.map((item: OrderModel) => ({
                idOrder: item.idOrder,
                customer: item.customer?.firstName,
                document: item?.customer?.document,
                total: item.total,
                created: formatDateString(item.created ?? ""),
                status: valueStatusConvert(item.status?.statusName ?? ""),
            }));

            exportToExcel(listExport, ["Número", "Cliente", 'Documento', "Valor Prévio", "Data de Criação", "Status"], "Tabela de Pedido.xlsx");
        }
    };

    return (
        <>
            <BreadCrumbComponent breadCrumbList={breadCrumb} />

            <Permission permissionKeys={[PermissionOrder.GET]} fallback={<PermissionComponent />}>
                <ContentBox title="Pedidos" description='Tela de listagem de Pedidos' className="mb-4 p-6">
                    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
                        {({ resetForm }) => (
                            <Form>
                                <div className="flex items-end w-full gap-4 pb-4">
                                    <InputComponet label="Número" name="idOrder" id="idOrder" />
                                    <InputComponet label="Cliente" name="customerName" id="customerName" />
                                    <SelectComponent label="Status" name="statusName" options={orderStatus} />
                                </div>

                                <div className="grid grid-cols-12 gap-6">
                                    <div className="col-span-4 mb-4">
                                        <SelectComponent label="Tipo de Pedido" name="bonus" options={orderTypes} />
                                    </div>
                                    <div className="col-span-4 mb-4">
                                        <InputComponet label="Documento" name="customerDocument" id="customerDocument" />
                                    </div>
                                    <div className="col-span-4 mb-4">
                                        <DatePickerComponet label="Data inicial" name="date" />
                                    </div>
                                </div>

                                <div className="flex items-end gap-4 justify-end">
                                    <Button onClick={() => handleClearAndSearch(resetForm)}>
                                        Limpar
                                    </Button>
                                    <Button htmlType="submit" type="primary">Filtrar</Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </ContentBox>
                <ContentBox className="mb-4 p-6" description='Tabela de Pedidos' actions={
                    <>
                        <ActionButton icon actionType="export" onClick={() => handleExport()}>Exportar para Excel</ActionButton>
                    </>
                }>
                    <Table
                        columns={columns}
                        className="table-custom"
                        dataSource={orderList.map((item: any, index: number) => ({
                            ...item,
                            key: item.uuid || index,
                        }))}
                        onChange={onChange}
                        loading={loading}
                        pagination={{
                            current: page + 1,
                            pageSize: size,
                            total: totalElements,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '40', '60'],
                        }}
                        size="middle"
                        showSorterTooltip={{ target: 'sorter-icon' }}
                    />
                </ContentBox>
            </Permission>

            {isModalOpen && <ModalCliente notViewClientUnitsActive setIsModalOpen={setIsModalOpen} isModalOpen={isModalOpen} customerUuid={itemSelected?.customer?.uuid} />}
        </>
    );
});

export default ListOrder;